import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, GALLERY_APP_URL, BI_APP_URL, WEEKLY_APP_URL, CONNECT_ENABLED } from '../config.js';
import { buildWeeklyUrl } from '../utils/weeklyTab.js';
import UserMenu from '../components/UserMenu.js';
import LoginForm from '../components/LoginForm.js';
import CockpitNavBar from '../components/CockpitNavBar.js';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { useAuth } from '../contexts/AuthContext.js';
import useAnalytics from '../hooks/useAnalytics.js';

const Cockpit = ({ fetchWithAuth, currentUser, logout, setCockpitContext, activeTab, fullscreen = false }) => {
  const { slug } = useParams();
  const [cockpit, setCockpit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localActiveTab, setLocalActiveTab] = useState(0);
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('darkMode') === 'true' ||
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [isForbidden, setIsForbidden] = useState(false);
  const [showManualLogin, setShowManualLogin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loginWithConnectOnce } = useAuth();

  // Use local state for tabs if in fullscreen mode
  const currentActiveTab = fullscreen ? localActiveTab : (activeTab || 0);

  // Check if current user is an admin
  const isAdmin = currentUser?.isadmin || currentUser?.issuperadmin;

  // Filter tabs based on admin status
  const visibleTabs = useMemo(() => {
    if (!cockpit?.config?.tabs) return [];
    return cockpit.config.tabs.filter(tab => !tab.admin_only || isAdmin);
  }, [cockpit, isAdmin]);

  // Map visible index to original tab index
  const getOriginalTabIndex = (visibleIndex) => {
    if (!cockpit?.config?.tabs) return 0;

    // Create a mapping of visible tabs to original tabs
    const visibleToOriginal = cockpit.config.tabs
      .map((tab, index) => ({ tab, index }))
      .filter(item => !item.tab.admin_only || isAdmin)
      .map(item => item.index);

    // Return the original index for the visible index
    return visibleToOriginal[visibleIndex] || 0;
  };

  // Map original index to visible tab index
  const getVisibleIndex = (originalIndex) => {
    if (!cockpit?.config?.tabs) return 0;

    // Count visible tabs before the original index
    return cockpit.config.tabs
      .slice(0, originalIndex)
      .filter(tab => !tab.admin_only || isAdmin)
      .length;
  };

  // Handle dark mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Function to navigate to admin edit page
  const navigateToAdminEdit = () => {
    navigate(`/admin/cockpits?edit=${cockpit.id}`);
  };

  // Extraire le slug de l'onglet depuis l'URL si présent
  useEffect(() => {
    if (cockpit?.config?.tabs && cockpit.config.tabs.length > 0) {
      // Vérifier si un hash est présent dans l'URL (#tab-slug)
      const tabSlug = location.hash.replace('#', '');
      if (tabSlug) {
        // Trouver l'index de l'onglet correspondant au slug
        const tabIndex = cockpit.config.tabs.findIndex(tab => tab.slug === tabSlug);
        if (tabIndex !== -1) {
          // Vérifier si l'utilisateur a accès à cet onglet
          const tab = cockpit.config.tabs[tabIndex];
          if (!tab.admin_only || isAdmin) {
            setLocalActiveTab(tabIndex);
          }
        }
      }
    }
  }, [cockpit, location.hash, isAdmin]);

  // Mise à jour de l'URL lors du changement d'onglet
  const handleTabChange = (tabIndex) => {
    if (cockpit?.config?.tabs && cockpit.config.tabs[tabIndex]) {
      const tabSlug = cockpit.config.tabs[tabIndex].slug;

      // Mettre à jour l'URL sans rechargement de page
      window.history.pushState(null, '', `#${tabSlug}`);
      setLocalActiveTab(tabIndex);

      // Pas besoin d'ajouter de code de tracking ici car le hook useAnalytics
      // détectera automatiquement le changement de hash et déclenchera le tracking
    } else {
      setLocalActiveTab(tabIndex);
    }
  };

  useEffect(() => {
    const fetchCockpit = async () => {
      try {
        setLoading(true);
        setRequiresAuth(false);
        setError(null);
        setIsForbidden(false);

        // Use a different endpoint for public cockpits that doesn't require authentication
        const endpoint = `${API_BASE_URL}/api/cockpits/${slug}`;

        let response;

        // If user is authenticated, use fetchWithAuth directly
        if (isAuthenticated) {
          try {
            response = await fetchWithAuth(endpoint);

            // Handle 403 - User is authenticated but doesn't have access rights
            if (response.status === 403) {
              setIsForbidden(true);
              setLoading(false);
              return;
            }
          } catch (err) {
            console.error('Error fetching cockpit with auth:', err);
            // Check if this is a 403 error
            if (err.message && err.message.includes('403')) {
              setIsForbidden(true);
            } else {
              setError(err.message || "Une erreur s'est produite lors du chargement du cockpit");
            }
            setLoading(false);
            return;
          }
        } else {
          // User is not authenticated, try public access first
          response = await fetch(endpoint);

          // If cockpit requires authentication (401), prompt login
          if (response.status === 401) {
            setRequiresAuth(true);
            setLoading(false);
            return;
          }
        }

        // Handle other error responses
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Cockpit non trouvé');
          }
          throw new Error('Erreur lors du chargement du cockpit');
        }

        // Process successful response
        const data = await response.json();
        setCockpit(data);

        // Set cockpit context in parent component if not in fullscreen mode
        if (!fullscreen && data.config && data.config.tabs && data.config.tabs.length > 0 && setCockpitContext) {
          setCockpitContext(slug, data.config.tabs, 0);
        }
      } catch (err) {
        console.error('Error fetching cockpit:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCockpit();

    // Cleanup function to reset cockpit context when unmounting
    return () => {
      if (!fullscreen && setCockpitContext) {
        setCockpitContext(null, null);
      }
    };
  }, [slug, fetchWithAuth, setCockpitContext, fullscreen, isAuthenticated]);

  // Cockpit privé, visiteur non authentifié : on l'envoie sur Connect au lieu de lui
  // faire cliquer un écran intermédiaire. Rebond déjà tenté dans cet onglet : on
  // retombe sur le formulaire manuel plutôt que de boucler.
  useEffect(() => {
    if (!requiresAuth || !CONNECT_ENABLED) return;
    if (!loginWithConnectOnce()) setShowManualLogin(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiresAuth]);

  // Get current tab
  const currentTab = (() => {
    if (!cockpit?.config?.tabs) return null;

    // Si l'utilisateur n'est pas admin et que le tab actif est un tab admin-only
    if (cockpit.config.tabs[currentActiveTab]?.admin_only && !isAdmin) {
      // Trouver le premier tab non admin-only
      const firstVisibleTab = cockpit.config.tabs.find(tab => !tab.admin_only);
      return firstVisibleTab || cockpit.config.tabs[0];
    }

    return cockpit.config.tabs[currentActiveTab] || cockpit.config.tabs[0];
  })();

  // Tracking des vues d'onglets avec Plausible
  useAnalytics({
    cockpitName: cockpit?.name,
    tabName: currentTab?.name,
    tabSlug: currentTab?.slug,
    currentUser: currentUser
  });

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${fullscreen ? 'h-screen' : 'h-[calc(100vh-64px)]'}`}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-xl font-medium text-gray-700 dark:text-gray-200">Chargement du cockpit...</div>
        </div>
      </div>
    );
  }

  // En mode legacy, le formulaire manuel est le seul chemin.
  if (requiresAuth) {
    return (
      <div className={`flex flex-col items-center justify-center ${fullscreen ? 'h-screen' : 'h-[calc(100vh-64px)]'}`}>
        {CONNECT_ENABLED && !showManualLogin ? (
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <div className="text-xl font-medium text-gray-700 dark:text-gray-200">Redirection vers la connexion…</div>
          </div>
        ) : (
          <div className="w-full max-w-md">
            <div className="mb-6 text-center">
              <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
                Accès restreint
              </h2>
            </div>
            <LoginForm authMessage="Veuillez vous connecter pour accéder à ce cockpit" />
          </div>
        )}
      </div>
    );
  }

  // Show forbidden message if user doesn't have access
  if (isForbidden) {
    return (
      <div className={`flex flex-col items-center justify-center ${fullscreen ? 'h-screen' : 'h-[calc(100vh-64px)]'}`}>
        <div className="w-full max-w-md text-center">
          <div className="mb-6 flex flex-col items-center">
            <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H9m3-3V8m0 0V6m0 2h2M9 10h2" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">
              Accès refusé
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Vous n'avez pas les droits nécessaires pour accéder à ce cockpit.
            </p>
            {currentUser && (
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Connecté en tant que <span className="font-medium">{currentUser.username}</span>
              </div>
            )}
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center ${fullscreen ? 'h-screen' : 'h-[calc(100vh-64px)]'}`}>
        <div className="text-xl font-medium text-red-600 dark:text-red-400 mb-4">{error}</div>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  if (!cockpit || !cockpit.config || !cockpit.config.tabs || cockpit.config.tabs.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center ${fullscreen ? 'h-screen' : 'h-[calc(100vh-64px)]'}`}>
        <div className="text-xl font-medium text-gray-700 dark:text-gray-200 mb-4">
          Ce cockpit ne contient aucun onglet
        </div>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }

  const renderTabContent = (tab) => {
    switch (tab.type) {
      case 'embed':
        return (
          <iframe
            src={tab.properties.src}
            title={tab.name}
            className="w-full h-full border-0"
            allowFullScreen
          />
        );
      case 'external': {
        // Utiliser un composant interne pour pouvoir utiliser useEffect
        const ExternalTabContent = () => {
          useEffect(() => {
            // Vérifier que nous sommes dans un environnement navigateur
            if (typeof window !== 'undefined' && tab.properties.src) {
              // Utiliser setTimeout pour s'assurer que le navigateur ne bloque pas l'ouverture
              setTimeout(() => {
                const newWindow = window.open(tab.properties.src, '_blank', 'noopener,noreferrer');

                // Vérifier si l'ouverture a été bloquée
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                  console.warn('L\'ouverture du lien a été bloquée par le navigateur');
                }
              }, 100);
            }
          }, []);

          return (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="text-xl font-medium text-gray-700 dark:text-gray-200 mb-6">
                Ce lien a été ouvert dans un nouvel onglet
              </div>
              <a
                href={tab.properties.src}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
              >
                <span>Ouvrir à nouveau {tab.name}</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          );
        };

        return <ExternalTabContent />;
      }
      case 'gallery':
        // Construire l'URL avec les projets en paramètres de requête
        const galleryUrl = new URL(`${GALLERY_APP_URL}/gallery`);

        // Ajouter chaque projet comme project[]=id dans l'URL
        if (tab.properties.projects && Array.isArray(tab.properties.projects)) {
          tab.properties.projects.forEach(projectId => {
            galleryUrl.searchParams.append('project[]', projectId);
          });
        }

        // add jail parameter to the url
        galleryUrl.searchParams.append('jail', 'true');

        return (
          <div className="h-full w-full">
            <iframe
              src={galleryUrl.toString()}
              className="w-full h-full border-0"
              title={`Gallery - ${tab.name}`}
              allow="fullscreen"
            />
          </div>
        );
      case 'gi-ai': {
        // Construire l'URL avec le clientId en paramètre de requête
        const giAiUrl = new URL(`${BI_APP_URL}/gi-ai-report-viewer`);

        if (tab.properties.clientId != null && tab.properties.clientId !== '') {
          giAiUrl.searchParams.append('clientId', tab.properties.clientId);
        }

        // add jail parameter to the url
        giAiUrl.searchParams.append('jail', 'true');

        return (
          <div className="h-full w-full">
            <iframe
              src={giAiUrl.toString()}
              className="w-full h-full border-0"
              title={`GI AI - ${tab.name}`}
              allow="fullscreen"
            />
          </div>
        );
      }
      case 'weekly':
        // Page du client dans Weekly, avec jail=true pour masquer les autres clients
        return (
          <div className="h-full w-full">
            <iframe
              src={buildWeeklyUrl(WEEKLY_APP_URL, tab.properties.clientSlug)}
              className="w-full h-full border-0"
              title={`Weekly - ${tab.name}`}
              allow="fullscreen"
            />
          </div>
        );
      default:
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-xl font-medium text-gray-700 dark:text-gray-200">
              Type d'onglet non pris en charge: {tab.type}
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`flex flex-col ${fullscreen ? 'h-screen' : 'h-[calc(100vh-64px)]'}`}>
      {/* Custom topbar for fullscreen mode */}
      {fullscreen && (
        <CockpitNavBar
          cockpit={cockpit}
          isAdmin={isAdmin}
          localActiveTab={localActiveTab}
          handleTabChange={handleTabChange}
          currentUser={currentUser}
          navigateToAdminEdit={navigateToAdminEdit}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          logout={logout}
        />
      )}

      {/* Tab content — un onglet vient d'une config arbitraire (`new URL()` sur une source
          invalide lève pendant le rendu) : il tombe seul, la barre reste là pour en changer.
          `key` sur l'onglet actif, sinon changer d'onglet garderait le repli affiché. */}
      <div className="flex-1 overflow-hidden h-full w-full">
        <ErrorBoundary key={currentActiveTab} title="Cet onglet n'a pas pu s'afficher">
          {renderTabContent(currentTab)}
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default Cockpit;